import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { MapPin, Clock, Users, Mountain, Camera, Heart } from 'lucide-react';
import Header from '../../components/ui/Header';
import TrailPhotoGallery from '../../components/ui/TrailPhotoGallery';
import Icon from '../../components/AppIcon';


const TrailDetailsInTheEmbraceOfForestAndThreeLakes = () => {
  const [activeTab, setActiveTab] = useState('attractions');

  // Trail data
  const trailData = {
    name: 'U zagrljaju šume i tri jezera',
    subtitle: 'In the Embrace of Forest and Three Lakes',
    country: 'Montenegro',
    region: 'Durmitor National Park',
    difficulty: 'Easy',
    length: '6 km',
    duration: '1 dan',
    theme: 'Forest Heritage',
    description: `Magične guste šume Durmitora, karakterističan miris četinara, tragovi stanovnika ove šumske zajednice i tišina koju tek ponekad prekine neka sova ili vrijedni djetlić, čekaju vas na četvrtoj tematskoj stazi. Prateći smjerokaze, ovdje se susrećete sa tri jezera. Ipak, to nije sve. Prateći potok, nailazite na ostatke brojnih mlinova, a o njima ćete pročitati više na informativnim tablama koje se nalaze duž staze. Uz sve navedeno, ova šumska tematska staza donosi hranu za sva čula.`,
    image: "https://res.cloudinary.com/dzt5ejpvb/image/upload/v1763504743/crno_jezero_xzpdft.jpg",
    imageAlt: 'Crno jezero okruženo gustom četinarskom šumom sa maglovitim planinama u pozadini'
  };

  // Natural attractions
  const naturalAttractions = [
  {
    id: 1,
    name: 'Masiv Durmitora',
    type: 'Planinski masiv',
    description: 'Masiv Durmitora, koji spada u remek djela prirode, proglašen je nacionalnim parkom 1952. godine. Planinsko područje je poznato po surovim uslovima, ali isto tako i po izuzetnoj ljepoti koja privlači pažnju posjetilaca. Obiluje planinskim vrhovima, od kojih je čak 48 iznad 2.000 m nadmorske visine, ima 18 ledničkih jezera, kanjone Tare, Drage i Sušice i četinare koji se pružaju dokle ljudsko oko doseže.',
    gps: '43.153°N, 19.101°E',
    image: "https://res.cloudinary.com/dzt5ejpvb/image/upload/v1763228225/Fotografija_4.1_-durmitorski_pejza%C5%BE_vgnskg.jpg",
    imageAlt: 'Dramatični durmitorski pejzaž sa visokim planinama i ledničkim jezerima'
  },
  {
    id: 2,
    name: 'Barno jezero',
    type: 'Ledničko jezero',
    description: 'Na putu ka Zminjem jezeru, kroz gustu četinarsku šumu, nailazi se na gotovo neprimjetno Barno jezero. Njegove obale su obrasle gustom barskom vegetacijom, pa je površina jezera veoma mala. Vjeruje se da je voda ovog jezera ljekovita i da naročito pomaže kod kožnih oboljenja. Maksimalna dubina ovog jezera je svega jedan metar.',
    gps: '43.157°N, 19.093°E',
    image: "https://res.cloudinary.com/dzt5ejpvb/image/upload/v1763228209/Fotografija_4.2_-_Barno_jezero_arf2nk.jpg",
    imageAlt: 'Barno jezero sa gustom barskom vegetacijom i ljekovitom vodom'
  },
  {
    id: 3,
    name: 'Zminje jezero',
    type: 'Ledničko jezero',
    description: 'Zminje jezero se nalazi na vrelu Mlinski potok, na sat vremena laganog pješačenja od Crnog jezera. Nalazi se na 1.520 metara nadmorske visine, a duboko je skoro 8 metara. Jezero je okruženo gustom šumom, tišinom, i neobične je, smaragdne boje. Legenda kaže da je na ovom prostoru nekada bilo mnogo zmija, koje su se tu skupljale zbog jakih zima, kako bi se ugrijale.',
    gps: '43.156°N, 19.070°E',
    image: "https://res.cloudinary.com/dzt5ejpvb/image/upload/v1763228199/Fotografija_4.3_-_Zminje_jezero_najdhg.jpg",
    imageAlt: 'Smaragdno Zminje jezero okruženo gustom četinarskom šumom'
  },
  {
    id: 4,
    name: 'Crno jezero',
    type: 'Ledničko jezero',
    description: 'Najveće i najpoznatije jezero Durmitora se nalazi 3 km od centra Žabljaka. Smješteno je ispod impresivnog vrha Međed, sa kojim čini atraktivan pejzaž koji svake godine privlači ogroman broj posjetilaca. Crno jezero je sastavljeno od dvije vodene površine – Malog i Velikog jezera, oko kojih ide laka pješačka staza. Dubina Malog jezera dostiže 49 metara, dok je dubina Velikog jezera upola manja i iznosi 24,5 metara.',
    gps: '43.147°N, 19.093°E',
    image: "https://res.cloudinary.com/dzt5ejpvb/image/upload/v1763228203/Fotografija_4.5_-_Crno_jezero_etopcq.jpg",
    imageAlt: 'Crno jezero sa Malim i Velikim jezerom ispod vrha Međed'
  },
  {
    id: 5,
    name: 'Flora i fauna Durmitora',
    type: 'Biodiverzitet',
    description: 'Durmitorski kraj je naseljen raznovrsnom faunom koja broji oko 300 zaštićenih vrsta. Na ovom području se može sresti više od 130 vrsta dnevnih leptira, među kojima je najpoznatiji Apolon, zaštićena vrsta. Stanovnici ovih gustih šuma su medvjed, srna, vuk, zec, divlja svinja, lisica i druge, a na surovim liticama se često mogu vidjeti i divokoze. Na području Durmitora je zastupljeno oko 1.300 vaskularnih biljaka.',
    gps: 'Nacionalni park Durmitor',
    image: "https://res.cloudinary.com/dzt5ejpvb/image/upload/v1763228197/Fotografija_4.6_-flora_Durmitora_x0rrlu.jpg",
    imageAlt: 'Bogata flora Durmitora sa endemskim biljkama i raznovrsnim ekosustavom'
  }];


  // Cultural attractions
  const culturalAttractions = [
  {
    id: 1,
    name: 'Tradicionalno graditeljstvo',
    type: 'Arhitektura',
    description: 'Tradicionalno graditeljstvo durmitorskog kraja. Dualitet materijala, kamen i drvo, od kojih su napravljene durmitorske kuće, prikazuje kako je lokalno stanovništvo uspjelo da iskoristi ono što mu je surova priroda ovog područja dala na najbolji mogući način. Gorštaci su kreirali nekoliko tipova brvnara koje su korištene za život porodice, obezbjeđivanje stoke i privremeni život tokom ljetnjeg perioda u planini (katuni).',
    gps: 'Durmitor region',
    image: "https://res.cloudinary.com/dzt5ejpvb/image/upload/v1763228195/Fotografija_4.7_-_tradicionalna_koliba_nxkuym.jpg",
    imageAlt: 'Tradicionalna drvena koliba sa kamenim osnovom u durmitorskom stilu'
  },
  {
    id: 2,
    name: 'Selo Bosača',
    type: 'Kulturno naselje',
    description: 'Na svega 4 km od Žabljaka, smjestilo se jedno od najljepših durmitorskih sela – Bosača. Bosača je jedinstvena jer je najviše stalno naseljeno mjesto na Balkanu, nalazi se na 1.600 metara nadmorske visine. Ispresijecano drvenim ogradama, usred atraktivnog pejzaža, Bosača je s razlogom omiljena među fotografima. Opasano Barnim, Zminjim i Jablan jezerom i gustom četinarskom šumom, ovo selo je idealno polazište za planinarske ture.',
    gps: '43.163°N, 19.087°E',
    image: "https://res.cloudinary.com/dzt5ejpvb/image/upload/v1763228200/Fotografija_4.8_-_selo_Bosa%C4%8Da_jknyk6.jpg",
    imageAlt: 'Selo Bosača sa tradicionalnim drvenim ogradama i planinama u pozadini'
  },
  {
    id: 3,
    name: 'Srednjovjekovna groblja – stećci',
    type: 'Arheološki spomenici',
    description: 'Mistična groblja, koja su od 2017. godine pod zaštitom Uneska, nalaze se u neposrednoj blizini Vražjeg i Ribljeg jezera. Poznatija i kao grčka/bogumilska groblja, stećci su nadgrobni spomenici iz perioda srednjovjekovne Bosne. Stećci se ne nalaze slučajno na ovim mjestima – prema predanju, vjerovalo se da su birana mjesta koja odišu posebnom energijom, na blagim uzvišenjima i u blizini planinskih jezera.',
    gps: '43.095°N, 19.150°E',
    image: "https://res.cloudinary.com/dzt5ejpvb/image/upload/v1763228213/Fotografija_4.9_-_Bare_%C5%BDugi%C4%87a_ste%C4%87ci_ypyrhu.jpg",
    imageAlt: 'Srednjovjekovni stećci sa uklesanim simbolima na planini Durmitor'
  },
  {
    id: 4,
    name: 'Tradicijska gastronomija',
    type: 'Kulturno nasljeđe',
    description: 'Gastronomiju sjevera Crne Gore karakterišu mesni i mlječni specijaliteti koji čine tradiciju ovog podneblja. Jagnjetina pečena ispod sača, ali i ostala mesa, obavezni su na svakoj trpezi, kao i mlječni proizvodi ‒ sir, kisjelo i slatko mlijeko, kajmak. Tu ima mjesta i za grilovanu pastrmku iz planinske rijeke, a specijaliteti poput kačamaka i cicvare fantastično idu uz domaće kisjelo mlijeko.',
    gps: 'Durmitor region',
    image: "https://res.cloudinary.com/dzt5ejpvb/image/upload/v1763228196/Fotografija_4.10_-_tradicionalna_kuhinja_uikiex.jpg",
    imageAlt: 'Tradicionalni durmitorski specijaliteti sa mesnim i mlječnim proizvodima'
  },
  {
    id: 5,
    name: 'Prirodnjački muzej NP Durmitor',
    type: 'Muzej',
    description: 'U upravnoj zgradi NP Durmitor, koja se nalazi na putu do Crnog jezera, pored Centra za posjetioce gdje se mogu dobiti sve potrebne informacije u vezi sa turističkom ponudom Parka, nalazi se i prirodnjačka zbirka, otvorena za sve posjetioce. U ovom mini-muzeju, posjetioci mogu dosta da saznaju o biljnom i životinjskom svijetu Durmitora, tradicionalnoj izradi predmeta od drveta.',
    gps: '43.151°N, 19.103°E',
    image: "https://res.cloudinary.com/dzt5ejpvb/image/upload/v1763504982/Centar-za-posjetioce-NP-Durmitor_kdwrfh.jpg",
    imageAlt: 'Prirodnjački muzej Nacionalnog parka Durmitor sa edukativnim sadržajima'
  }];


  // Itinerary options
  const itineraryOptions = [
  {
    id: 'ranger-path',
    title: 'KAKO POSTATI RENDŽER IZ SJENE',
    subtitle: 'Za ljubitelje prirode koji žele da što bolje upoznaju šumu',
    duration: '1 dan',
    description: 'Ruta: Centar za posjetioce NP Durmitor – Barno jezero – Zminje jezero – Crno jezero',
    activities: [
    'Posjetu šumi započnite obilaskom Centra za posjetioce NP Durmitor, gdje ćete se upoznati o biljnom i životinjskom svijetu nacionalnog parka',
    'Pješačenjem prema Ivan dolu dolazite do tematske staze "U zagrljaju šume tri jezera". U dužini od 6 km markiranom šumskom stazom obilaze se: Barno, Zminje, i Crno jezero',
    'Kod Barnog jezera – otkrijte biljne vrste iz ledenog doba',
    'Kod raskrsnice planinskih staza – započnite igru trakera divljih šumskih životinja, odavde obratite pažnju na zvukove šume',
    'Kod Zminjeg jezera – fotografišite nekoliko mladih biljaka i životinja oko jezera',
    'Uz Mlinski potok – upoznajte Prašumu i ako imate smjelosti, pođite na noćno posmatranje sova',
    'Kod Jakšića mlina – otkrijte kako je Durmitor izgledao prije 10.000 godina']

  },
  {
    id: 'mythical-paths',
    title: 'MITSKI PUTEVI DURMITORA',
    subtitle: 'Za one koji žele da upoznaju ljude i tradiciju durmitorskog kraja',
    duration: '1 dan',
    description: 'Ruta: Pirlitor – Stećci – Riblje i Vražje jezero',
    activities: [
    'Jutarnje otkrivanje mitskog Durmitora - Vožnja od Žabljaka magistralnim putem do sela Vrela, 8 km',
    'Pješačenje 3 km do mitskog grada Pirlitora, na rubu Jezerske visoravni i na uzvišenju iznad kanjona Tare',
    'Vožnja do nekropola bogumilskih stećaka, 14 km',
    'Obilazak srednjovjekovne nekropola stećaka i Ribljeg i Vražjeg jezera, u čijim dubinama, prema legendama, još uvijek gospodare mračne sile',
    'Popodnevno otkrivanje izvornih seoskih domaćinstava - Obilazak seoskih domaćinstava sa akcentom na domaćinstvima koja njeguju ručni rad, izradu gusli, slikanje tehnikom filcanja vune',
    'Pješačenje tematskom stazom "U zagrljaju šume i tri jezera" i posjeta selu Bosača, najvišem naselju na Balkanu']

  }];


  // Service providers
  const serviceProviders = [
  {
    id: 1,
    name: 'Mićun Karadžić',
    type: 'Ručni rad - gusle',
    description: 'Mićun Karadžić aktivno izrađuje gusle posljednjih 20 godina. U pitanju je ručni rad. Gusle nastaju od javorovog drveta, za gudalo se koristi isključivo konjska dlaka, a u ovoj porodici postoji duga tradicija rada sa drvetom. Na guslama se nalaze crnogorski motivi: lik Njegoša, Marka Miljanova, grb, pejzaž Durmitora itd.',
    address: 'Ulica Narodnih heroja 32, Žabljak',
    phone: '+382 69 223 221',
    gps: '43.157°N, 19.122°E',
    image: "https://res.cloudinary.com/dzt5ejpvb/image/upload/v1763405235/7._Mi%C4%87un_Karad%C5%BEi%C4%87_lsoxgc.jpg",
    imageAlt: 'Tradicionalne gusle ručne izrade sa crnogorskim motivima'
  },
  {
    id: 2,
    name: 'Branka Pajović',
    type: 'Umjetnost - filcanje vune',
    description: 'Branka, jedna od dvije harfistkinje u Crnoj Gori, sa keltskom harfom u ambijentu Durmitora stvara magiju, ali njene ruke kreiraju još jednu umjetnost. Tehnikom filcanja vune, Branka pravi suvenire i umjetničke slike sa motivima Durmitora. Na ovaj način kreira autentične suvenire koje ćete rado ponijeti sa sobom.',
    address: 'Novakovići bb, Žabljak',
    phone: '+382 67 654 928',
    gps: '43.091°N, 19.163°E',
    image: "https://res.cloudinary.com/dzt5ejpvb/image/upload/v1763405235/8._Branka_Pajovi%C4%87_iztnyu.jpg",
    imageAlt: 'Umjetnička slika od filcane vune sa durmitorskim motivima'
  },
  {
    id: 3,
    name: 'Domaćinstvo Krejović',
    type: 'Seosko domaćinstvo',
    description: 'Krejovići žive u selu Borje, koje je samo par stotina metara udaljeno od skretanja sa magistralnog puta za Pirlitor. Njihovo dvorište redovno ugošćava kampere, a ono što treba da probate je domaće suvo meso i sirevi, a posebno domaći trapist po kome je domaćica prepoznata.',
    address: 'Borje bb, Žabljak',
    phone: '+382 67 344 237',
    email: 'tanjakrejovic93@gmail.com',
    gps: '43.166°N, 19.184°E',
    image: "https://res.cloudinary.com/dzt5ejpvb/image/upload/v1763405245/13._Doma%C4%87instvo_Krejovi%C4%87_qhb4zy.jpg",
    imageAlt: 'Tradicionalno seosko domaćinstvo sa domaćim proizvodima i smještajem'
  },
  {
    id: 4,
    name: 'Domaćinstvo Kaljević',
    type: 'Seosko domaćinstvo sa folklorom',
    description: 'Kako bi vam dočarali duh starih vremena, Kaljevići za svoje goste organizuju muzičke večeri sa članovima folklora u crnogorskoj narodnoj nošnji, uz izvođenja na tradicionalnim instrumentima ‒ guslama i sviralama. Za ljubitelje boravka u prirodi, ovo domaćinstvo može organizovati jahanje konja.',
    address: 'Podgora bb, Žabljak',
    phone: '+382 67 024 044',
    email: 'laki.k@t-com.me',
    gps: '43.198°N, 19.146°E',
    image: "https://res.cloudinary.com/dzt5ejpvb/image/upload/v1763405248/14._Doma%C4%87instvo_Kaljevi%C4%87_hpwlqi.jpg",
    imageAlt: 'Folklorni nastup u tradicionalnim crnogorskim nošnjama'
  },
  {
    id: 5,
    name: 'Chalets Pod Gorom',
    type: 'Eko kamp',
    description: 'U ovom eko kampu možete pronaći smještaj u udobnim bungalovima, a prava atrakcija je porodična kuća, izgrađena na jedinstven način. Po želji gostiju, domaćinstvo pruža i uslugu hrane. Domaćin je dobar poznavalac okoline, pa za zainteresovane goste može organizovati i razne izlete.',
    address: 'Njegovuđa, Žabljak',
    phone: '+382 67 344 721',
    email: 'chaletspodgorom@t-com.me',
    gps: '43.119°N, 19.218°E',
    image: "https://res.cloudinary.com/dzt5ejpvb/image/upload/v1763405248/15._Chalets_Pod_Gorom_tab636.jpg",
    imageAlt: 'Eko kamp sa udobnim bungalovima u planинском ambijentu'
  }];


  // Gallery images
  const galleryImages = [
  {
    id: 1,
    src: "https://res.cloudinary.com/dzt5ejpvb/image/upload/v1763505215/Masiv_Durmitora_kqddrj.jpg",
    alt: 'Durmitorski pejzaž sa planinama i jezerima',
    caption: 'Masiv Durmitora'
  },
  {
    id: 2,
    src: "https://res.cloudinary.com/dzt5ejpvb/image/upload/v1763505259/barno-jezero-01-8_fgfc2x.webp",
    alt: 'Barno jezero sa barskom vegetacijom',
    caption: 'Barno jezero'
  },
  {
    id: 3,
    src: "https://res.cloudinary.com/dzt5ejpvb/image/upload/v1763505303/1703255903-119_oeh93c.jpg",
    alt: 'Smaragdno Zminje jezero',
    caption: 'Zminje jezero'
  },
  {
    id: 4,
    src: "https://res.cloudinary.com/dzt5ejpvb/image/upload/v1763505372/crno_jezero_ispod_vrhe_medjed_zxgz2m.jpg",
    alt: 'Crno jezero ispod vrha Međed',
    caption: 'Crno jezero'
  },
  {
    id: 5,
    src: "https://res.cloudinary.com/dzt5ejpvb/image/upload/v1763505404/unnamed_x6vph0.jpg",
    alt: 'Selo Bosača sa drvenim ogradama',
    caption: 'Selo Bosača'
  },
  {
    id: 6,
    src: "https://res.cloudinary.com/dzt5ejpvb/image/upload/v1763505449/13st_Radimlja_os6fzp.jpg",
    alt: 'Srednjovjekovni stećci',
    caption: 'Stećci - UNESCO zaštita'
  }];


  const renderTabContent = () => {
    switch (activeTab) {
      case 'attractions':
        return (
          <div className="space-y-8">
            {/* Natural Attractions */}
            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                <Mountain className="w-5 h-5 mr-2 text-green-600" />
                Prirodne atrakcije
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {naturalAttractions?.map((attraction) =>
                <div key={attraction?.id} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                    <img
                    src={attraction?.image}
                    alt={attraction?.imageAlt}
                    className="w-full h-48 object-cover" />

                    <div className="p-4">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-semibold text-gray-900">{attraction?.name}</h4>
                        <span className="text-sm text-blue-600 bg-blue-50 px-2 py-1 rounded-full">
                          {attraction?.type}
                        </span>
                      </div>
                      <p className="text-gray-600 text-sm mb-3 line-clamp-3">
                        {attraction?.description}
                      </p>
                      <div className="flex items-center text-sm text-gray-500">
                        <MapPin className="w-4 h-4 mr-1" />
                        {attraction?.gps}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Cultural Attractions */}
            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                <Heart className="w-5 h-5 mr-2 text-red-600" />
                Kulturno-istorijske atrakcije
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {culturalAttractions?.map((attraction) =>
                <div key={attraction?.id} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                    <img
                    src={attraction?.image}
                    alt={attraction?.imageAlt}
                    className="w-full h-48 object-cover" />

                    <div className="p-4">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-semibold text-gray-900">{attraction?.name}</h4>
                        <span className="text-sm text-purple-600 bg-purple-50 px-2 py-1 rounded-full">
                          {attraction?.type}
                        </span>
                      </div>
                      <p className="text-gray-600 text-sm mb-3 line-clamp-3">
                        {attraction?.description}
                      </p>
                      <div className="flex items-center text-sm text-gray-500">
                        <MapPin className="w-4 h-4 mr-1" />
                        {attraction?.gps}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>);


      case 'itinerary':
        return (
          <div className="space-y-6">
            {itineraryOptions?.map((option) =>
            <div key={option?.id} className="bg-white rounded-lg border border-gray-200 p-6">
                <div className="mb-4">
                  <h3 className="text-xl font-semibold text-gray-900 mb-1">{option?.title}</h3>
                  <p className="text-gray-600 mb-2">{option?.subtitle}</p>
                  <div className="flex items-center text-sm text-gray-500 mb-3">
                    <Clock className="w-4 h-4 mr-1" />
                    {option?.duration}
                  </div>
                  <p className="text-gray-700 font-medium">{option?.description}</p>
                </div>
                <div className="space-y-2">
                  {option?.activities?.map((activity, index) =>
                <div key={index} className="flex items-start">
                      <span className="text-blue-600 mr-2">•</span>
                      <p className="text-gray-600 text-sm">{activity}</p>
                    </div>
                )}
                </div>
              </div>
            )}
          </div>);


      case 'services':
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {serviceProviders?.map((provider) =>
            <div key={provider?.id} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                <img
                src={provider?.image}
                alt={provider?.imageAlt}
                className="w-full h-48 object-cover" />

                <div className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold text-gray-900">{provider?.name}</h4>
                    <span className="text-sm text-green-600 bg-green-50 px-2 py-1 rounded-full">
                      {provider?.type}
                    </span>
                  </div>
                  <p className="text-gray-600 text-sm mb-3 line-clamp-3">
                    {provider?.description}
                  </p>
                  <div className="space-y-1 text-sm text-gray-500">
                    <div className="flex items-center">
                      <MapPin className="w-4 h-4 mr-1" />
                      {provider?.address}
                    </div>
                    <div>{provider?.phone}</div>
                    {provider?.email && <div>{provider?.email}</div>}
                  </div>
                </div>
              </div>
            )}
          </div>);


      case 'gallery':
        return <TrailPhotoGallery images={galleryImages} />;

      default:
        return null;
    }
  };

  return (
    <>
      <Helmet>
        <title>U zagrljaju šume i tri jezera - Trail Details | Creative Twinning</title>
        <meta
          name="description"
          content="Istražite magične guste šume Durmitora kroz tematsku stazu koja povezuje tri ledničke jezera. Otkrijte Barno, Zminje i Crno jezero uz bogatu floru i faunu Nacionalnog parka Durmitor." />

        <meta name="keywords" content="Durmitor, tri jezera, šuma, Crno jezero, Zminje jezero, Barno jezero, Montenegro, priroda" />
      </Helmet>
      <div className="min-h-screen bg-gray-50">
        <Header />
        
        <main className="pt-16">
          {/* Hero Section */}
          <div className="relative h-96 bg-gray-900">
            <img
              src={trailData?.image}
              alt={trailData?.imageAlt}
              className="w-full h-full object-cover opacity-70" />

            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-8">
              <div className="max-w-7xl mx-auto">
                <div className="text-white">
                  <h1 className="text-4xl font-bold mb-2">{trailData?.name}</h1>
                  <p className="text-xl text-gray-200 mb-4">{trailData?.subtitle}</p>
                  <div className="flex flex-wrap gap-4 text-sm">
                    <div className="flex items-center">
                      <MapPin className="w-4 h-4 mr-1" />
                      {trailData?.region}, {trailData?.country}
                    </div>
                    <div className="flex items-center">
                      <Mountain className="w-4 h-4 mr-1" />
                      {trailData?.difficulty}
                    </div>
                    <div className="flex items-center">
                      <Clock className="w-4 h-4 mr-1" />
                      {trailData?.duration}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Content Section */}
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
              {/* Main Content */}
              <div className="lg:col-span-3">
                {/* Trail Description */}
                <div className="bg-white rounded-lg border border-gray-200 p-6 mb-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">O stazi</h2>
                  <p className="text-gray-700 leading-relaxed">{trailData?.description}</p>
                </div>

                {/* Tab Navigation */}
                <div className="bg-white rounded-lg border border-gray-200 overflow-hidden mb-8">
                  <div className="border-b border-gray-200">
                    <nav className="flex space-x-8 px-6" aria-label="Tabs">
                      {[
                      { id: 'attractions', label: 'Atrakcije', icon: Mountain },
                      { id: 'itinerary', label: 'Predlog obilaska', icon: Clock },
                      { id: 'services', label: 'Pružaoci usluga', icon: Users },
                      { id: 'gallery', label: 'Galerija', icon: Camera }]?.
                      map(({ id, label, icon: Icon }) =>
                      <button
                        key={id}
                        onClick={() => setActiveTab(id)}
                        className={`${
                        activeTab === id ?
                        'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center`
                        }>

                          <Icon className="w-4 h-4 mr-2" />
                          {label}
                        </button>
                      )}
                    </nav>
                  </div>
                  
                  <div className="p-6">
                    {renderTabContent()}
                  </div>
                </div>
              </div>

              {/* Sidebar */}
              <div className="lg:col-span-1">
                {/* Trail Info Card */}
                <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Osnovne informacije</h3>
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Dužina:</span>
                      <span className="font-medium">{trailData?.length}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Vrijeme:</span>
                      <span className="font-medium">{trailData?.duration}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Težina:</span>
                      <span className="font-medium">{trailData?.difficulty}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Tema:</span>
                      <span className="font-medium">{trailData?.theme}</span>
                    </div>
                  </div>
                </div>

                {/* Highlights */}
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Ključni objekti</h3>
                  <ul className="space-y-2 text-sm">
                    {['Barno jezero', 'Zminje jezero', 'Crno jezero', 'Selo Bosača', 'Tradicionalno graditeljstvo']?.map((highlight, index) =>
                    <li key={index} className="flex items-center text-gray-700">
                        <span className="w-1.5 h-1.5 bg-blue-600 rounded-full mr-3"></span>
                        {highlight}
                      </li>
                    )}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </>);

};

export default TrailDetailsInTheEmbraceOfForestAndThreeLakes;